import React from "react";
import moment from "moment";
import locale from "@bonliva-crm/locale";
import { isEmpty } from "lodash";
import { durationsToWeeks, weeksToDurations } from "@bonliva-crm/common";
import { Overlay } from "@components";
import {
  WeekSpanGrid,
  PeriodColors,
  Legend,
  WeekSpanGridProps
} from "@components/SpanGrid";

type Props = Omit<WeekSpanGridProps, "spans" | "start" | "end"> & {
  availability?: CustomDuration[];
  bookings?: CustomDuration[];
  disabled?: boolean;
  disableNotBooked?: boolean;
  disabledWeeks?: Date[];
  durations?: CustomDuration[];
  end?: Date;
  start?: Date;
  weekParams?: any[];
  contentAfter?: React.ReactNode;
  skipDisabledSpans?: boolean;
  onHover?: (week: Date, e: React.MouseEvent) => void;
  requested?: CustomDuration[];
  isBookingGroupContext?: boolean;
};

export const WorkRequestPeriods = React.memo((props: Props) => {
  const {
    availability,
    bookings,
    disabled,
    disableNotBooked,
    disabledWeeks = [],
    durations,
    requested,
    weekParams,
    end,
    onChange,
    onClick,
    readonly,
    start,
    value
  } = props;

  const combine = (arr: CustomDuration[], color: string) =>
    (arr || []).map((x) => ({ ...x, color, colorMix: 0.5 }));

  const bookingSpans = combine(bookings, PeriodColors.Booked);
  const durationSpans = combine(durations, PeriodColors.WorkRequest);
  const availabilitySpans = combine(availability, PeriodColors.Availability);
  const requestedSpans = combine(requested, PeriodColors.WorkRequest);
  const allSpans = bookingSpans.concat(durationSpans, availabilitySpans, requestedSpans);

  const bookedWeeks = durationsToWeeks(bookings || []).map((x) => x.valueOf());

  const min = allSpans.min((x) => new Date(x.From).getTime());
  const max = allSpans.max((x) => new Date(x.To).getTime());

  const _disabledWeeks = disabledWeeks.map((x) => x.valueOf());

  const bookingSpansLegend = props.isBookingGroupContext ? locale.Booked : locale.Week;

  return (
    <div style={{ position: "relative" }}>
      <Overlay in={disabled} />
      <WeekSpanGrid
        readonly={readonly}
        start={moment.utc(start || (min ? min.From : new Date())).startOf("isoWeek")}
        end={moment.utc(end || (max ? max.To : new Date())).endOf("isoWeek")}
        spans={allSpans}
        value={value}
        onChange={(_durations) => {
          if (!start && !end) {
            return onChange?.(_durations);
          }

          if (!start || !end) {
            throw new Error("expected both props.start and props.end");
          }

          const currentWeeks = durationsToWeeks(value);
          const weeksBeyondCurrentPage = currentWeeks.filter(
            (x) => x < start || x >= end
          );
          const currentPage = durationsToWeeks(_durations);

          onChange(weeksToDurations([...weeksBeyondCurrentPage, ...currentPage]));
        }}
        onClick={onClick}
        renderCellContent={(date, color) => {
          if (color === PeriodColors.WorkRequest && weekParams) {
            const weekParam = weekParams.find(
              (x) => x.week === moment(date).week() && x.year === moment(date).year()
            );
            return weekParam ? weekParam.openVacancies.toString() : "";
          }
          return null;
        }}
        isCellDisabled={(date, color) => {
          let result = false;

          if (disableNotBooked) {
            result = result || !bookedWeeks.includes(date.valueOf());
          }

          if (color === PeriodColors.WorkRequest && weekParams) {
            const weekParam = weekParams.find(
              (x) => x.week === moment(date).week() && x.year === moment(date).year()
            );

            result = result || weekParam?.isDisabled;
          }

          return result;
        }}
        isGroupDisabled={(v) => {
          let result = false;

          if (disableNotBooked) {
            result = result || !bookedWeeks.includes(v.valueOf());
          }

          if (!isEmpty(disabledWeeks)) {
            result = result || _disabledWeeks.includes(v.valueOf());
          }

          return result;
        }}
        skipDisabledSpans={props.skipDisabledSpans}
        onHover={(date, e) => props.onHover?.(date, e)}
      />

      <Legend
        legends={[
          bookingSpans.any() && {
            color: PeriodColors.Booked,
            legend: bookingSpansLegend
          },
          durationSpans.any() && {
            color: PeriodColors.WorkRequest,
            legend: locale.Period
          },
          availabilitySpans.any() && {
            color: PeriodColors.Availability,
            legend: locale.Availability
          },
          requestedSpans.any() && {
            color: PeriodColors.WorkRequest,
            legend: locale.AccommodationRequested
          },
          ((value && value.any()) || !readonly) && {
            color: PeriodColors.Selected,
            legend: locale.Selected
          }
        ].filter(Boolean)}
      />

      {props.contentAfter}
    </div>
  );
});
